import { useTheme } from '@mui/material'
import useMediaQuery from '@mui/material/useMediaQuery'

/**
 * Responsive column configuration matching MUI breakpoints.
 * Used by WindowVirtualizedList and VirtualizedList for multi-column layouts.
 */
export type ResponsiveColumns = {
  xs?: number
  sm?: number
  md?: number
  lg?: number
  xl?: number
}

/**
 * Resolves the current column count from a fixed number or responsive config.
 * Uses MUI breakpoints; when columns is an object, returns the value for the
 * current breakpoint or the first defined fallback.
 *
 * @param columns - Fixed number (e.g. 2) or breakpoint map (e.g. { xs: 1, lg: 2 })
 * @returns Column count for the current viewport (always >= 1)
 */
export const useResponsiveColumns = (columns: number | ResponsiveColumns | undefined): number => {
  const theme = useTheme()
  const isXs = useMediaQuery(theme.breakpoints.only('xs'))
  const isSm = useMediaQuery(theme.breakpoints.only('sm'))
  const isMd = useMediaQuery(theme.breakpoints.only('md'))
  const isLg = useMediaQuery(theme.breakpoints.only('lg'))
  const isXl = useMediaQuery(theme.breakpoints.only('xl'))

  if (typeof columns === 'number') {
    return Math.max(1, columns)
  }

  if (!columns) {
    return 1
  }

  if (isXl && columns.xl !== undefined) return Math.max(1, columns.xl)
  if (isLg && columns.lg !== undefined) return Math.max(1, columns.lg)
  if (isMd && columns.md !== undefined) return Math.max(1, columns.md)
  if (isSm && columns.sm !== undefined) return Math.max(1, columns.sm)
  if (isXs && columns.xs !== undefined) return Math.max(1, columns.xs)

  return Math.max(1, columns.xl ?? columns.lg ?? columns.md ?? columns.sm ?? columns.xs ?? 1)
}
